import { type YDatabase } from '@databases/collab/y-database';
import { type IntlShape } from 'react-intl-next';

import { cardToGalleryLayout } from './migrations/card-to-gallery-layout.ts';
import { filterId } from './migrations/filter-id.ts';
import { groupFieldIdToGroupingSettings } from './migrations/group-field-id-to-grouping-settings.ts';
import { initialDoc } from './migrations/initial-doc.ts';
import { initialView } from './migrations/initial-view.ts';
import { newNumberFormat } from './migrations/new-number-format.ts';
import { parentPageContentId } from './migrations/parent-page-content-id.ts';
import { sortToMultiSort } from './migrations/sort-to-multi-sort.ts';
import { transformAttachments } from './migrations/transform-attachments.ts';
import { transformViews } from './migrations/transform-views.ts';

interface Migration {
  readonly version: number;

  apply(
    documentId: string,
    database: YDatabase,
    intlShape: IntlShape
  ): void | Promise<void>;
}

export class MigrationManager {
  private static readonly migrations: Migration[] = [
    initialDoc,
    parentPageContentId,
    initialView,
    transformViews,
    transformAttachments,
    filterId,
    sortToMultiSort,
    newNumberFormat,
    cardToGalleryLayout,
    groupFieldIdToGroupingSettings
  ];

  constructor(
    private readonly documentId: string,
    private readonly database: YDatabase,
    private readonly intlShape: IntlShape,
    private readonly remoteVersion: number
  ) {}

  async runPending() {
    for (const pendingMigration of this.pendingMigrations) {
      await pendingMigration.apply(this.documentId, this.database, this.intlShape);
    }
  }

  static getClientDocVersion(): number {
    const latestMigration = MigrationManager.migrations.reduce((prev, current) =>
      prev.version > current.version ? prev : current
    );
    return latestMigration.version;
  }

  private get pendingMigrations() {
    return MigrationManager.migrations.filter(
      (migration) => migration.version > this.remoteVersion
    );
  }
}
